import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/hooks/useConfig";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Send, Trash2, Upload } from "lucide-react";

const pretty = s => s ? String(s).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "";
const IMAGE_SLOTS = [["front", "Front"], ["back", "Back"], ["side", "Side"], ["nutrition_label", "Nutrition Label"], ["ingredients_label", "Ingredients Label"]];

const emptySku = () => ({
  line_id: "", variant_name: "", net_quantity: "", weight_unit: "ml", pack_format: "Single Unit",
  units_per_pack: 1, barcode_ean: "", mrp: 0, hsn_code: "", gst_percent: 18, gst_treatment: "Exclusive",
  units_per_inner_box: 0, units_per_master_carton: 0, carton_dimensions: { l: "", w: "", h: "" }, carton_weight_kg: 0,
  unit_dimensions: { l: "", w: "", h: "" }, unit_weight_g: 0, moq: 1, buy_price: 0, suggested_selling_price: 0,
  primary_packaging: "", secondary_packaging: "",
});

export default function BrandProductsPage() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).some(r => ["Admin", "admin"].includes(r));
  const can = p => isAdmin || perms.includes(p);
  const canBuyPrice = can("brands.view_buy_price");
  const { getItems } = useConfig();

  const [brand, setBrand] = useState(null);
  const [listings, setListings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendErrors, setSendErrors] = useState(null);
  const [catOptions, setCatOptions] = useState([]);

  const load = useCallback(() => {
    api.get(`/brands/${brandId}`).then(r => setBrand(r.data)).catch(() => {});
    api.get(`/brands/${brandId}/listings`).then(r => setListings(r.data || [])).catch(() => {});
  }, [brandId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/brands/categories").then(r => setCatOptions(r.data || [])).catch(() => {}); }, []);

  const opts = g => (getItems(g) || []).map(i => i.value || i.key);
  const weightUnits = opts("weight_units");
  const packFormats = opts("pack_formats");
  const foodTypes = opts("food_types");
  const gstRates = (getItems("gst_rates") || []).map(i => i.key);
  const gstTreatments = opts("gst_treatments");
  const primaryPack = opts("primary_packaging");
  const secondaryPack = opts("secondary_packaging");
  const storageConditions = opts("brand_storage_conditions");

  const emptyListing = () => ({
    product: {
      product_name: "", category_id: "", sub_category: "", description: "", key_features: [""],
      ingredients: "", nutrition_info: "", allergen_info: "", food_type: "Vegetarian",
      storage_condition: "", shelf_life_days: 0, country_of_origin: "India",
      manufacturer_name: "", fssai_number: "", images: { lifestyle: [] },
    },
    skus: [emptySku()],
  });

  const setP = (key, val) => setEditing(e => ({ ...e, product: { ...e.product, [key]: val } }));
  const setS = (i, key, val) => setEditing(e => { const a = [...e.skus]; a[i] = { ...a[i], [key]: val }; return { ...e, skus: a }; });
  const setDim = (i, field, axis, val) => setEditing(e => { const a = [...e.skus]; a[i] = { ...a[i], [field]: { ...(a[i][field] || {}), [axis]: val } }; return { ...e, skus: a }; });

  const uploadImage = async (slot, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("slot", slot);
    try {
      const r = await api.post(`/brands/${brandId}/listing-images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (slot === "lifestyle") {
        setP("images", { ...(editing.product.images || {}), lifestyle: [...((editing.product.images || {}).lifestyle || []), r.data.url] });
      } else {
        setP("images", { ...(editing.product.images || {}), [slot]: r.data.url });
      }
      toast.success(`${pretty(slot)} image uploaded`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const saveListing = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = { product: { ...editing.product, key_features: (editing.product.key_features || []).filter(Boolean) }, skus: editing.skus };
      if (editing._id) await api.put(`/brands/${brandId}/listings/${editing._id}`, payload);
      else await api.post(`/brands/${brandId}/listings`, payload);
      toast.success("Listing saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const sendToCatalogue = async (listingId) => {
    setSendErrors(null);
    try {
      const r = await api.post(`/brands/${brandId}/listings/${listingId}/send-to-catalogue`);
      toast.success(r.data.message);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.errors) { setSendErrors({ id: listingId, items: detail.errors, title: "Fill these in before sending:" }); toast.error("Some fields are missing"); }
      else if (detail?.clashes) { setSendErrors({ id: listingId, items: detail.clashes, title: "Nothing was created — fix these clashes:" }); toast.error("Clashing SKU names or barcodes"); }
      else toast.error(typeof detail === "string" ? detail : "Send failed");
    }
  };

  if (!brand) return <p className="text-xs text-zinc-400 p-4">Loading...</p>;

  return (
    <div className="space-y-4" data-testid="brand-products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">{brand.display_name} — Products</h1>
          <p className="text-xs text-zinc-500">{listings.length} product listing(s)</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(`/brands/${brandId}`)}>Back to Brand</Button>
          {can("brands.listings.manage") && <Button size="sm" onClick={() => setEditing(emptyListing())} data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" /> Add Product</Button>}
        </div>
      </div>

      {!editing && listings.map(l => (
        <div key={l._id} className="border rounded-md p-4" data-testid={`listing-${l._id}`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="font-semibold text-sm">{l.product?.product_name || "Unnamed"}</span>
              <Badge className={`ml-2 text-[10px] ${l.listing_status === "sent_to_catalogue" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{pretty(l.listing_status)}</Badge>
              <span className="ml-2 text-xs text-zinc-500">{(l.skus || []).length} SKU(s)</span>
              <span className="ml-2 text-xs text-zinc-500" data-testid={`completeness-${l._id}`}>{l.completeness || 0}% complete</span>
              {l.catalogue_product_id && <span className="ml-2 text-xs text-blue-600">Catalogue: {l.catalogue_product_id}</span>}
            </div>
            <div className="flex gap-2">
              {l.listing_status === "draft" && can("brands.listings.manage") && (
                <Button size="sm" variant="outline" data-testid={`edit-listing-${l._id}`} onClick={() => setEditing({
                  ...l,
                  product: { lifestyle: [], ...(l.product || {}), key_features: (l.product?.key_features || [""]) },
                  skus: (l.skus || []).map(s => ({ ...s, mrp: (s.mrp_paise || 0) / 100, buy_price: (s.buy_price_paise || 0) / 100, suggested_selling_price: (s.suggested_selling_price_paise || 0) / 100 })),
                })}>Edit</Button>
              )}
              {l.listing_status === "draft" && can("brands.listings.push") && (
                <Button size="sm" onClick={() => sendToCatalogue(l._id)} disabled={brand.lifecycle_status !== "live"} title={brand.lifecycle_status !== "live" ? "Approve the brand first" : ""} data-testid={`send-catalogue-${l._id}`}>
                  <Send className="w-3.5 h-3.5 mr-1" /> Send to Catalogue
                </Button>
              )}
              {l.listing_status === "sent_to_catalogue" && <p className="text-xs text-zinc-500 italic" data-testid={`sent-note-${l._id}`}>Edit this in Product Catalogue</p>}
            </div>
          </div>
          {sendErrors?.id === l._id && (
            <div className="border border-red-200 bg-red-50 rounded-md p-2 mb-2" data-testid="send-errors">
              <p className="text-xs font-medium text-red-700">{sendErrors.title}</p>
              {sendErrors.items.map((m, i) => <p key={i} className="text-xs text-red-600">- {m}</p>)}
            </div>
          )}
          {l.listing_status === "sent_to_catalogue" ? (
            <div className="overflow-x-auto" data-testid={`live-skus-${l._id}`}>
              <table className="w-full text-xs border">
                <thead className="bg-zinc-100"><tr><th className="px-2 py-1 text-left">Catalogue SKU</th><th className="px-2 py-1 text-left">Name</th><th className="px-2 py-1">MRP</th><th className="px-2 py-1">Approval</th></tr></thead>
                <tbody>
                  {(l.catalogue_skus || []).map(cs => (
                    <tr key={cs.catalogue_sku_id} className="border-t">
                      <td className="px-2 py-1 font-mono">{cs.catalogue_sku_id}</td>
                      <td className="px-2 py-1">{cs.sku_name}</td>
                      <td className="px-2 py-1 text-right">₹{cs.mrp}</td>
                      <td className="px-2 py-1 text-center"><Badge className="text-[10px] bg-amber-100 text-amber-700">{pretty(cs.approval_status)}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-zinc-500">
              {(l.skus || []).map((s, i) => <span key={i} className="mr-3">{s.variant_name || `SKU #${i + 1}`} — MRP ₹{(s.mrp_paise || 0) / 100}</span>)}
            </div>
          )}
        </div>
      ))}

      {editing && (
        <div className="border rounded-md p-4 space-y-4 bg-zinc-50" data-testid="listing-form">
          <h3 className="text-sm font-semibold">Product Details</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Product Name <span className="text-red-500">*</span></Label>
              <Input className="h-8 text-xs" value={editing.product.product_name || ""} onChange={e => setP("product_name", e.target.value)} data-testid="listing-product-name" />
            </div>
            <div>
              <Label className="text-xs">Category <span className="text-red-500">*</span></Label>
              <select className="w-full h-8 text-xs border rounded-md px-2" value={editing.product.category_id || ""} onChange={e => setP("category_id", e.target.value)} data-testid="listing-category">
                <option value="">Select...</option>
                {catOptions.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Sub-category</Label><Input className="h-8 text-xs" value={editing.product.sub_category || ""} onChange={e => setP("sub_category", e.target.value)} /></div>
            <div>
              <Label className="text-xs">Food Type</Label>
              <select className="w-full h-8 text-xs border rounded-md px-2" value={editing.product.food_type || ""} onChange={e => setP("food_type", e.target.value)} data-testid="listing-food-type">
                {foodTypes.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Storage Condition</Label>
              <select className="w-full h-8 text-xs border rounded-md px-2" value={editing.product.storage_condition || ""} onChange={e => setP("storage_condition", e.target.value)}>
                <option value="">Select...</option>
                {storageConditions.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Shelf Life (days)</Label><Input className="h-8 text-xs" type="number" value={editing.product.shelf_life_days || 0} onChange={e => setP("shelf_life_days", parseInt(e.target.value) || 0)} /></div>
            <div><Label className="text-xs">Country of Origin</Label><Input className="h-8 text-xs" value={editing.product.country_of_origin || ""} onChange={e => setP("country_of_origin", e.target.value)} /></div>
            <div><Label className="text-xs">Manufacturer Name</Label><Input className="h-8 text-xs" value={editing.product.manufacturer_name || ""} onChange={e => setP("manufacturer_name", e.target.value)} /></div>
            <div><Label className="text-xs">FSSAI Number</Label><Input className="h-8 text-xs" value={editing.product.fssai_number || ""} onChange={e => setP("fssai_number", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Description</Label><textarea className="w-full border rounded-md p-2 text-xs h-14" value={editing.product.description || ""} onChange={e => setP("description", e.target.value)} /></div>
            <div><Label className="text-xs">Ingredients</Label><textarea className="w-full border rounded-md p-2 text-xs h-14" value={editing.product.ingredients || ""} onChange={e => setP("ingredients", e.target.value)} data-testid="listing-ingredients" /></div>
            <div><Label className="text-xs">Nutrition Info</Label><textarea className="w-full border rounded-md p-2 text-xs h-14" value={editing.product.nutrition_info || ""} onChange={e => setP("nutrition_info", e.target.value)} data-testid="listing-nutrition" /></div>
            <div><Label className="text-xs">Allergen Info</Label><textarea className="w-full border rounded-md p-2 text-xs h-14" value={editing.product.allergen_info || ""} onChange={e => setP("allergen_info", e.target.value)} /></div>
          </div>
          <div>
            <Label className="text-xs">Key Features</Label>
            {(editing.product.key_features || []).map((f, i) => (
              <div key={i} className="flex gap-2 mt-1">
                <Input className="h-8 text-xs" value={f} onChange={e => { const a = [...editing.product.key_features]; a[i] = e.target.value; setP("key_features", a); }} data-testid={`key-feature-${i}`} />
                <button className="text-red-400" onClick={() => setP("key_features", editing.product.key_features.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="mt-1" onClick={() => setP("key_features", [...(editing.product.key_features || []), ""])} data-testid="add-key-feature"><Plus className="w-3 h-3 mr-1" /> Add Feature</Button>
          </div>
          <div>
            <Label className="text-xs">Images (png/jpg, max 2 MB each)</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {IMAGE_SLOTS.map(([slot, label]) => (
                <div key={slot} className="border rounded-md p-2 bg-white">
                  <p className="text-[11px] text-zinc-500 mb-1">{label}</p>
                  <input type="file" accept="image/png,image/jpeg" className="text-[10px]" onChange={e => uploadImage(slot, e.target.files?.[0])} data-testid={`upload-${slot}`} />
                  {(editing.product.images || {})[slot] && <p className="text-[10px] text-emerald-600 mt-1">Uploaded</p>}
                </div>
              ))}
              <div className="border rounded-md p-2 bg-white">
                <p className="text-[11px] text-zinc-500 mb-1">Lifestyle (multiple)</p>
                <input type="file" accept="image/png,image/jpeg" className="text-[10px]" onChange={e => uploadImage("lifestyle", e.target.files?.[0])} data-testid="upload-lifestyle" />
                <p className="text-[10px] text-zinc-400 mt-1">{((editing.product.images || {}).lifestyle || []).length} uploaded</p>
              </div>
            </div>
          </div>

          <h3 className="text-sm font-semibold mt-3">SKUs</h3>
          <div className="overflow-x-auto border rounded-md bg-white" data-testid="sku-table-wrap">
            <table className="text-xs" style={{ minWidth: "1700px" }}>
              <thead className="bg-zinc-100">
                <tr>
                  <th className="px-2 py-1 text-left">Variant *</th>
                  <th className="px-2 py-1">Net Qty</th>
                  <th className="px-2 py-1">Unit</th>
                  <th className="px-2 py-1">Pack Format</th>
                  <th className="px-2 py-1">Units/Pack</th>
                  <th className="px-2 py-1">EAN</th>
                  <th className="px-2 py-1">MRP *</th>
                  <th className="px-2 py-1">HSN *</th>
                  <th className="px-2 py-1">GST % *</th>
                  <th className="px-2 py-1">GST Treatment</th>
                  <th className="px-2 py-1">Inner Box</th>
                  <th className="px-2 py-1">Carton *</th>
                  <th className="px-2 py-1">Carton L×W×H</th>
                  <th className="px-2 py-1">Carton kg</th>
                  <th className="px-2 py-1">Unit L×W×H</th>
                  <th className="px-2 py-1">Unit g</th>
                  <th className="px-2 py-1">MOQ</th>
                  {canBuyPrice && <th className="px-2 py-1">Buy ₹</th>}
                  {canBuyPrice && <th className="px-2 py-1">Suggested ₹</th>}
                  <th className="px-2 py-1">Primary Pack</th>
                  <th className="px-2 py-1">Secondary Pack</th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {editing.skus.map((s, i) => (
                  <tr key={i} className="border-t" data-testid={`sku-row-${i}`}>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-28" value={s.variant_name || ""} onChange={e => setS(i, "variant_name", e.target.value)} data-testid={`sku-variant-${i}`} /></td>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-16" value={s.net_quantity || ""} onChange={e => setS(i, "net_quantity", e.target.value)} /></td>
                    <td className="px-1 py-1"><select className="h-7 text-xs border rounded w-16" value={s.weight_unit || ""} onChange={e => setS(i, "weight_unit", e.target.value)}>{weightUnits.map(u => <option key={u} value={u}>{u}</option>)}</select></td>
                    <td className="px-1 py-1"><select className="h-7 text-xs border rounded w-28" value={s.pack_format || ""} onChange={e => setS(i, "pack_format", e.target.value)}>{packFormats.map(u => <option key={u} value={u}>{u}</option>)}</select></td>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-14" type="number" value={s.units_per_pack ?? 1} onChange={e => setS(i, "units_per_pack", parseInt(e.target.value) || 0)} /></td>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-28" value={s.barcode_ean || ""} onChange={e => setS(i, "barcode_ean", e.target.value)} data-testid={`sku-barcode-${i}`} /></td>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-16" type="number" value={s.mrp ?? 0} onChange={e => setS(i, "mrp", parseFloat(e.target.value) || 0)} data-testid={`sku-mrp-${i}`} /></td>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-20" value={s.hsn_code || ""} onChange={e => setS(i, "hsn_code", e.target.value)} data-testid={`sku-hsn-${i}`} /></td>
                    <td className="px-1 py-1"><select className="h-7 text-xs border rounded w-16" value={String(s.gst_percent ?? 18)} onChange={e => setS(i, "gst_percent", parseFloat(e.target.value))} data-testid={`sku-gst-${i}`}>{gstRates.map(g => <option key={g} value={g}>{g}%</option>)}</select></td>
                    <td className="px-1 py-1"><select className="h-7 text-xs border rounded w-24" value={s.gst_treatment || ""} onChange={e => setS(i, "gst_treatment", e.target.value)}>{gstTreatments.map(g => <option key={g} value={g}>{g}</option>)}</select></td>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-14" type="number" value={s.units_per_inner_box ?? 0} onChange={e => setS(i, "units_per_inner_box", parseInt(e.target.value) || 0)} /></td>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-14" type="number" value={s.units_per_master_carton ?? 0} onChange={e => setS(i, "units_per_master_carton", parseInt(e.target.value) || 0)} data-testid={`sku-carton-${i}`} /></td>
                    <td className="px-1 py-1 flex gap-0.5">
                      {["l", "w", "h"].map(ax => <Input key={ax} className="h-7 text-xs w-11" value={(s.carton_dimensions || {})[ax] || ""} onChange={e => setDim(i, "carton_dimensions", ax, e.target.value)} />)}
                    </td>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-14" type="number" value={s.carton_weight_kg ?? 0} onChange={e => setS(i, "carton_weight_kg", parseFloat(e.target.value) || 0)} /></td>
                    <td className="px-1 py-1 flex gap-0.5">
                      {["l", "w", "h"].map(ax => <Input key={ax} className="h-7 text-xs w-11" value={(s.unit_dimensions || {})[ax] || ""} onChange={e => setDim(i, "unit_dimensions", ax, e.target.value)} />)}
                    </td>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-14" type="number" value={s.unit_weight_g ?? 0} onChange={e => setS(i, "unit_weight_g", parseFloat(e.target.value) || 0)} /></td>
                    <td className="px-1 py-1"><Input className="h-7 text-xs w-12" type="number" value={s.moq ?? 1} onChange={e => setS(i, "moq", parseInt(e.target.value) || 0)} /></td>
                    {canBuyPrice && <td className="px-1 py-1"><Input className="h-7 text-xs w-16" type="number" value={s.buy_price ?? 0} onChange={e => setS(i, "buy_price", parseFloat(e.target.value) || 0)} data-testid={`sku-buy-price-${i}`} /></td>}
                    {canBuyPrice && <td className="px-1 py-1"><Input className="h-7 text-xs w-16" type="number" value={s.suggested_selling_price ?? 0} onChange={e => setS(i, "suggested_selling_price", parseFloat(e.target.value) || 0)} /></td>}
                    <td className="px-1 py-1"><select className="h-7 text-xs border rounded w-28" value={s.primary_packaging || ""} onChange={e => setS(i, "primary_packaging", e.target.value)}><option value="">—</option>{primaryPack.map(p => <option key={p} value={p}>{p}</option>)}</select></td>
                    <td className="px-1 py-1"><select className="h-7 text-xs border rounded w-28" value={s.secondary_packaging || ""} onChange={e => setS(i, "secondary_packaging", e.target.value)}><option value="">—</option>{secondaryPack.map(p => <option key={p} value={p}>{p}</option>)}</select></td>
                    <td className="px-1 py-1">{editing.skus.length > 1 && <button className="text-red-400" onClick={() => setEditing(e => ({ ...e, skus: e.skus.filter((_, j) => j !== i) }))}><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button size="sm" variant="outline" onClick={() => setEditing(e => ({ ...e, skus: [...e.skus, emptySku()] }))} data-testid="add-sku-row-btn"><Plus className="w-3 h-3 mr-1" /> Add SKU</Button>
          <div className="flex gap-2 pt-2">
            <Button onClick={saveListing} disabled={saving} data-testid="save-listing-btn">{saving ? "Saving..." : "Save Listing"}</Button>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
